import {
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

import axios from "../api_inbusiness/axios";

const initialState = {
  user: {},
  permissions: [],
  accessToken: undefined,
  setAccessToken: () => {},
};

const UserContext = createContext(initialState);

export const UserProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken")
  );
  const [user, setUser] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [loadingUser, setLoadingUser] = useState(true);
  const [permissionsData, setPermissionsData] = useState({
    seeSection: false,
    seeAdminButtons: false,
    participateEvent: false,
    seeVolunteerSections: false,
  });

  const me = async () => {
    try {
      const response = await axios.get("/users/me");
      checkPermission(response.data.type);
      return response.data;
    } catch (error) {
      // Handle authentication errors (401/403) by clearing the invalid token
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        console.log("Token expired or invalid, clearing authentication");
        localStorage.removeItem("accessToken");
        setAccessToken(undefined);
      }
      return {};
    }
  };

  const getUpdatedUser = async () => {
    const refreshUser = await me();

    setUser(refreshUser);
  };

  const can = useCallback(
    (permissionId) => {
      return 1;
    },
    [permissions]
  );

  const checkPermission = (type) => {
    switch (type) {
      case "MEMBER":
        permissionsData.seeSection = true;
        permissionsData.seeAdminButtons = false;
        permissionsData.becomeMember = false;
        permissionsData.participateEvent = true;
        permissionsData.seeVolunteerSections = false;

        break;
      case "EX_MEMBER":
        permissionsData.seeSection = false;
        permissionsData.seeAdminButtons = false;
        permissionsData.becomeMember = true;
        permissionsData.participateEvent = false;
        permissionsData.seeVolunteerSections = false;
        break;
      case "GUEST":
        permissionsData.seeSection = false;
        permissionsData.seeAdminButtons = false;
        permissionsData.becomeMember = true;
        permissionsData.participateEvent = true;
        permissionsData.seeVolunteerSections = false;
        break;
      case "LEADER":
        permissionsData.seeSection = true;
        permissionsData.seeAdminButtons = true;
        permissionsData.becomeMember = false;
        permissionsData.participateEvent = true;
        permissionsData.seeVolunteerSections = false;
        break;
      case "MANAGER":
        permissionsData.seeSection = true;
        permissionsData.seeAdminButtons = true;
        permissionsData.becomeMember = false;
        permissionsData.participateEvent = true;
        permissionsData.seeVolunteerSections = false;
        break;
      case "VOLUNTEER":
        permissionsData.seeSection = false;
        permissionsData.seeAdminButtons = false;
        permissionsData.becomeMember = false;
        permissionsData.participateEvent = false;
        permissionsData.seeVolunteerSections = true;
        break;
      default:
        permissionsData.seeSection = false;
        permissionsData.seeAdminButtons = false;
        permissionsData.becomeMember = false;
        permissionsData.participateEvent = false;
        permissionsData.seeVolunteerSections = false;
        break;
    }
  };

  const handleAccessTokenChange = async () => {
    if (!user.id && accessToken) {
      localStorage.setItem("accessToken", accessToken);
      axios.defaults.headers.common.Authorization = "Bearer " + accessToken;

      setLoadingUser(true);
      const userData = await me();

      // If user data is empty (authentication failed), don't set it
      if (userData.id) {
        /**
         * [FIX]
         * We have to explicitly check if user has a role (only for older users)
         * There may be some users that do not have a roleId
         */
        // if (userData.roleId) {
        //   const { data } = await RolesAPI.getById(userData.roleId);
        //   setPermissions(data);
        // }

        setUser(userData);
      } else {
        // Authentication failed, clear the invalid token
        localStorage.removeItem("accessToken");
        setAccessToken(undefined);
      }
    } else if (!accessToken) {
      /**
       * Log out
       * Reset important values
       */
      localStorage.removeItem("accessToken");
      setUser({});
      setPermissions([]);
    }

    setLoadingUser(false);
  };

  useEffect(() => {
    handleAccessTokenChange();
  }, [accessToken]);

  return (
    <UserContext.Provider
      value={{
        user,
        loadingUser,
        accessToken,
        setAccessToken,
        can,
        getUpdatedUser,
        permissionsData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
